/**
 * Writing module interface component.
 *
 * Main interface for writing module with essay writing and fill-in-the-blank chunks.
 * Supports chunk navigation, word count validation, and timer integration.
 * Fetches real data from backend and saves answers properly.
 *
 * @module WritingInterface
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ListChecks } from "lucide-react";
import { ExamHeader } from "@/components/exam/ExamHeader";
import { WritingPromptPanel } from "./WritingPromptPanel";
import { WritingEditor } from "./WritingEditor";
import { WritingFormPanel } from "./WritingFormPanel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useWritingModuleState } from "@/hooks/useWritingModuleState";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";
import type {
  ExamType,
  CEFRLevel,
  SimulationSettings,
} from "@/types/exam-simulation";
import { SimulationQuestionNavigatorDialog } from "@/components/exam/SimulationQuestionNavigatorDialog";
import { AdaptiveQuestionStateBadge } from "@/components/exam/AdaptiveQuestionStateBadge";
import { useSimulationInactivityMonitor } from "@/hooks/useSimulationInactivityMonitor";
import { SIMULATION_INACTIVITY_TIMEOUT_MS } from "@/lib/simulationRuntimeConfig";
import { SimulationInactivityDialog } from "@/components/exam/SimulationInactivityDialog";
import {
  buildAllWritingNavigatorItems,
  type BuildWritingNavParams,
  getEssayWordCount,
  getNavigatorProgressCounts,
  getWritingFinishValidationFromItems,
  getWritingJumpChunkIndex,
} from "@/utils/simulationQuestionNavigation";
import { SimulationUnansweredJumpChips } from "@/components/exam/SimulationUnansweredJumpChips";
import {
  ResizableModulePanels,
  SimulationChunkNavigation,
  SimulationModuleError,
  SimulationModuleLoading,
  SimulationModuleStatusBar,
  SimulationSavingOverlay,
} from "@/components/exam/shared";
import { formatSimulationSectionTitle } from "@/utils/simulationSectionTitle";
import {
  incompleteModuleDialogTitle,
  submitModuleButtonLabel,
  submitModuleConfirmLabel,
} from "@/lib/simulationSubmitModuleLabels";
import { useIsLargeScreen } from "@/hooks/use-large-screen";

interface WritingInterfaceProps {
  readonly examType: ExamType;
  readonly level: CEFRLevel;
  readonly settings: SimulationSettings | null;
  readonly onModuleFinish?: () => void;
  /** Session ID shared across all modules in a simulation */
  readonly sessionId?: string | null;
}

export function WritingInterface({
  examType,
  level,
  settings,
  onModuleFinish,
  sessionId,
}: WritingInterfaceProps) {
  const { t } = useTranslation();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showIncompleteConfirmDialog, setShowIncompleteConfirmDialog] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasTimerExpired, setHasTimerExpired] = useState(false);
  const [activeTab, setActiveTab] = useState<"prompt" | "task">("prompt");
  const [writingNavOpen, setWritingNavOpen] = useState(false);
  const isLargeScreen = useIsLargeScreen();
  const inactivity = useSimulationInactivityMonitor({
    enabled: !isSaving,
    timeoutMs: SIMULATION_INACTIVITY_TIMEOUT_MS,
  });

  const {
    state,
    essayTasks,
    currentEssayTask,
    currentEssayContent,
    fillBlankForms,
    hasFillBlank,
    hasEssay,
    totalChunks,
    isLoadingData,
    dataError,
    currentNotes,
    setCurrentEssayContent,
    setFillBlankAnswer,
    setCurrentNotes,
    navigateChunk,
    goToChunk,
    saveAnswersAndResult,
  } = useWritingModuleState({
    examType,
    level,
    settings,
    sessionId,
  });

  // Handle chunk change
  useEffect(() => {
    setActiveTab("prompt"); // Reset tab on chunk change
  }, [state.currentChunk]);

  // Default word target (no upper limit is enforced — the writer can keep
  // adding words past the suggested target).
  const minWords = 150;

  // Handle timer expiration
  useEffect(() => {
    if (state.timeRemaining === 0 && !hasTimerExpired && !isSaving) {
      setHasTimerExpired(true);
      handleSaveAndFinish();
    }
  }, [state.timeRemaining, hasTimerExpired, isSaving]);

  /**
   * Save answers and result, then proceed to finish
   */
  const handleSaveAndFinish = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      // Save all answers and result
      await saveAnswersAndResult();

      // Call onModuleFinish callback after successful save
      if (onModuleFinish) {
        onModuleFinish();
      }
    } catch (error) {
      logger.error("Error saving writing module answers", error);
      toast.error(
        t("simulation.errors.saveFailed", {
          defaultValue:
            "Failed to save your answers. Please try again or contact support if the problem persists.",
        }),
      );
      // Don't proceed to next step if save failed
      setIsSaving(false);
      setHasTimerExpired(false);
    }
  };

  const handleFinish = () => {
    if (isSaving) {
      return; // Prevent multiple clicks while saving
    }

    if (finishValidation.shouldConfirmUnanswered) {
      setShowIncompleteConfirmDialog(true);
      return;
    }

    if (finishValidation.shouldConfirmUnderWordLimit) {
      setShowConfirmDialog(true);
      return;
    }

    handleSaveAndFinish();
  };

  const handleConfirmFinish = () => {
    setShowConfirmDialog(false);
    handleSaveAndFinish();
  };

  const handleConfirmIncompleteFinish = () => {
    setShowIncompleteConfirmDialog(false);
    handleSaveAndFinish();
  };

  const isTimeWarning = state.timeRemaining < 5 * 60 && state.timeRemaining > 0;

  const moduleDisplayName = t("simulation.instructions.modules.writing", {
    defaultValue: "Writing",
  });
  const sectionTitle = formatSimulationSectionTitle(
    examType,
    level,
    moduleDisplayName,
  );

  // Determine which chunk type is currently active. Layout:
  //   chunk 0       = fill-in-blank (when `hasFillBlank`)
  //   chunks i>=fbb = one essay task per chunk, in `essayTasks` order
  // No level-specific branching — the count follows the data.
  const essayBaseChunk = hasFillBlank ? 1 : 0;
  const currentChunkType: "fill-in-blank" | "essay" | null = useMemo(() => {
    if (hasFillBlank && state.currentChunk === 0) return "fill-in-blank";
    if (
      hasEssay &&
      state.currentChunk >= essayBaseChunk &&
      state.currentChunk < essayBaseChunk + essayTasks.length
    ) {
      return "essay";
    }
    return null;
  }, [
    hasFillBlank,
    hasEssay,
    state.currentChunk,
    essayBaseChunk,
    essayTasks.length,
  ]);

  const currentAdaptiveState = useMemo(() => {
    if (currentChunkType === "fill-in-blank") {
      return fillBlankForms[0]?.adaptiveState ?? "new";
    }
    if (currentChunkType === "essay") {
      return currentEssayTask?.adaptiveState ?? "new";
    }
    return "new";
  }, [currentChunkType, fillBlankForms, currentEssayTask]);

  const essayTaskIds = useMemo(
    () => essayTasks.map((t) => t.questionId),
    [essayTasks],
  );

  const writingNavParams = useMemo<BuildWritingNavParams>(
    () => ({
      hasFillBlank,
      hasEssay,
      fillBlankForms,
      fillBlankAnswers: state.fillBlankAnswers,
      essayTasks,
      essayContentByTask: state.essayContentByTask,
      blankLabel: (n) =>
        t("simulation.active.writingBlankLabel", {
          n,
          defaultValue: "Blank {{n}}",
        }),
      essayLabel: (index, task) =>
        essayTasks.length > 1
          ? t("simulation.writing.tasks.essayN", {
              n: index,
              defaultValue: "Essay Task {{n}}",
            })
          : t("simulation.writing.tasks.essay", { defaultValue: "Essay Task" }),
    }),
    [
      hasFillBlank,
      hasEssay,
      fillBlankForms,
      state.fillBlankAnswers,
      essayTasks,
      state.essayContentByTask,
      t,
    ],
  );

  const writingNavItems = useMemo(
    () => buildAllWritingNavigatorItems(writingNavParams),
    [writingNavParams],
  );

  const finishValidation = useMemo(
    () =>
      getWritingFinishValidationFromItems({
        items: writingNavItems,
        hasEssay,
        essayContent: currentEssayContent,
        minWords,
      }),
    [writingNavItems, hasEssay, currentEssayContent, minWords],
  );

  const writingProgress = useMemo(
    () => getNavigatorProgressCounts(writingNavItems),
    [writingNavItems],
  );

  const unansweredNavItems = finishValidation.unansweredItems;

  const essayWordCount = useMemo(
    () => getEssayWordCount(currentEssayContent),
    [currentEssayContent],
  );

  const taskOverviewButton =
    writingNavItems.length > 0 ? (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 text-xs font-ui font-semibold"
        onClick={() => setWritingNavOpen(true)}
        aria-label={t("simulation.active.writingTaskOverview", {
          defaultValue: "Task overview",
        })}
      >
        <ListChecks className="h-4 w-4 shrink-0" aria-hidden />
        {t("simulation.active.writingTaskOverview", {
          defaultValue: "Task overview",
        })}
      </Button>
    ) : null;

  // Mobile task tab: show an unanswered indicator dot whenever the active chunk
  // has any empty answer (fill-in-blank) or an empty essay. Uses the shared
  // navigator utility so counts stay aligned with the Task overview dialog.
  const hasUnansweredInCurrentChunk = useMemo(() => {
    if (currentChunkType === "fill-in-blank") {
      return writingNavItems.some(
        (item) => item.id.startsWith("blank:") && !item.isAnswered,
      );
    }
    if (currentChunkType === "essay" && currentEssayTask) {
      const currentEssayItemId = `essay:${currentEssayTask.questionId}`;
      return writingNavItems.some(
        (item) => item.id === currentEssayItemId && !item.isAnswered,
      );
    }
    return false;
  }, [currentChunkType, currentEssayTask, writingNavItems]);

  const handleWritingNavJump = useCallback(
    (itemId: string) => {
      goToChunk(getWritingJumpChunkIndex(itemId, hasFillBlank, essayTaskIds));
      setActiveTab("task");
      setWritingNavOpen(false);
      globalThis.setTimeout(() => {
        if (itemId.startsWith("blank:")) {
          const blankId = itemId.slice(6);
          const el = document.querySelector<HTMLElement>(
            `[data-writing-blank-id="${blankId}"]`,
          );
          el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
          el?.focus();
        } else if (itemId.startsWith("essay:")) {
          const el = document.querySelector<HTMLElement>(
            '[data-writing-focus="essay"]',
          );
          el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
          el?.focus();
        }
      }, 220);
    },
    [goToChunk, hasFillBlank, essayTaskIds, setActiveTab],
  );

  const handleReviewUnanswered = () => {
    setShowIncompleteConfirmDialog(false);
    const firstUnanswered = unansweredNavItems[0];
    if (firstUnanswered) {
      handleWritingNavJump(firstUnanswered.id);
    }
  };

  if (isLoadingData) {
    return (
      <SimulationModuleLoading
        message={t("simulation.errors.loadingModule", {
          module: "Writing",
          defaultValue: "Loading writing module...",
        })}
      />
    );
  }

  if (dataError) {
    return (
      <SimulationModuleError
        message={t("simulation.errors.loadFailure", {
          moduleName: "Writing",
          attempts: 1,
          errorMessage: "Unknown",
          defaultValue: "Failed to load writing module",
        })}
        detail={
          dataError.message ||
          t("simulation.errors.unknownError", {
            defaultValue:
              "An error occurred while loading the writing module data.",
          })
        }
        hint={t("simulation.errors.tryAgain", {
          defaultValue:
            "Please try refreshing the page or contact support if the problem persists.",
        })}
      />
    );
  }

  // Show empty state if no data
  if (!hasFillBlank && !hasEssay) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-background">
        <Card className="p-8 max-w-md">
          <div className="flex flex-col items-center gap-4 text-center">
            <p className="text-muted-foreground font-ui">
              {t("simulation.writing.noContent", {
                defaultValue:
                  "No writing questions available for this level and exam type.",
              })}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const chunkNavigation =
    totalChunks > 1 ? (
      <SimulationChunkNavigation
        currentIndex={state.currentChunk}
        totalChunks={totalChunks}
        onPrevious={() => navigateChunk("prev")}
        onNext={() => navigateChunk("next")}
        previousLabel={t("common.previous", { defaultValue: "Previous Task" })}
        nextLabel={t("common.next", { defaultValue: "Next Task" })}
        className="bg-card/50"
      />
    ) : null;

  let promptPanel: JSX.Element | null = null;
  let taskPanel: JSX.Element | null = null;

  if (currentChunkType === "fill-in-blank" && fillBlankForms.length > 0) {
    const currentForm = fillBlankForms[0];

    promptPanel = (
      <WritingPromptPanel
        prompt={currentForm.prompt}
        taskType={t("simulation.writing.tasks.fillInBlanks", {
          defaultValue: "Fill in the Blanks",
        })}
        notes={currentNotes}
        onNotesChange={setCurrentNotes}
        navigationSlot={chunkNavigation}
      />
    );

    taskPanel = (
      <WritingFormPanel
        segments={currentForm.segments}
        blanks={currentForm.blanks}
        answers={state.fillBlankAnswers}
        onAnswerChange={setFillBlankAnswer}
        progressCurrent={writingProgress.answered}
        progressTotal={writingProgress.total}
        headerRightSlot={taskOverviewButton}
      />
    );
  } else if (currentChunkType === "essay" && currentEssayTask) {
    promptPanel = (
      <WritingPromptPanel
        prompt={currentEssayTask.prompt}
        imageUrl={currentEssayTask.imageUrl}
        taskType={currentEssayTask.taskType}
        notes={currentNotes}
        onNotesChange={setCurrentNotes}
        navigationSlot={chunkNavigation}
      />
    );

    taskPanel = (
      <WritingEditor
        content={currentEssayContent}
        onChange={setCurrentEssayContent}
        minWords={minWords}
        isTimeWarning={isTimeWarning}
        progressCurrent={writingProgress.answered}
        progressTotal={writingProgress.total}
        headerRightSlot={taskOverviewButton}
      />
    );
  }

  const writingPanels =
    promptPanel && taskPanel ? (
      isLargeScreen ? (
        <ResizableModulePanels
          autoSaveId="simulation-writing-horizontal-panels"
          left={promptPanel}
          right={taskPanel}
          leftDefaultSize={30}
          rightDefaultSize={70}
          leftMinSize={25}
          rightMinSize={45}
        />
      ) : (
        <>
          <div
            className={cn(
              activeTab === "prompt"
                ? "flex-1 flex flex-col overflow-hidden"
                : "hidden",
            )}
          >
            {promptPanel}
          </div>
          <div
            className={cn(
              "flex-1 flex-col overflow-hidden",
              activeTab === "task" ? "flex" : "hidden",
            )}
          >
            {taskPanel}
          </div>
        </>
      )
    ) : (
      <div className="flex-1 flex items-center justify-center">
        <Card className="p-8">
          <p className="text-muted-foreground font-ui">
            {t("simulation.writing.noContentChunk", {
              defaultValue: "No content available for this chunk.",
            })}
          </p>
        </Card>
      </div>
    );

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-background">
      <ExamHeader
        sectionTitle={sectionTitle}
        currentChunk={state.currentChunk}
        totalChunks={totalChunks}
        timeRemaining={state.timeRemaining}
        onFinish={handleFinish}
        disabled={isSaving}
        chunkLabel={t("simulation.active.chunkLabel.task", {
          defaultValue: "Task",
        })}
        finishModule="writing"
      />

      <SimulationModuleStatusBar
        className="bg-muted/20 px-3 py-1 sm:px-4 sm:py-1.5"
        left={<AdaptiveQuestionStateBadge state={currentAdaptiveState} />}
      />

      {/* Task overview dialog — trigger now lives inside WritingFormPanel /
          WritingEditor headers (rightSlot) so the overview button sits next to
          the task title, matching Reading/Listening's pattern. */}
      <SimulationQuestionNavigatorDialog
        open={writingNavOpen}
        onOpenChange={setWritingNavOpen}
        items={writingNavItems}
        onJump={handleWritingNavJump}
        jumpEnabled
      />

      {/* Main Content: Responsive Layout */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Mobile View Toggle (Tabs): Prompt (notes/prompt) | Task.
            The Task tab shows an unanswered indicator dot whenever the
            current chunk has any empty fill-in-blank answer or an empty essay. */}
        <div className="lg:hidden flex border-b bg-muted/30 shrink-0">
          <button
            className={cn(
              "flex-1 py-3 font-ui text-sm font-semibold transition-colors border-b-2",
              activeTab === "prompt"
                ? "border-primary text-primary bg-background"
                : "border-transparent text-muted-foreground",
            )}
            onClick={() => setActiveTab("prompt")}
          >
            {t("simulation.active.passage", { defaultValue: "Task Prompt" })}
          </button>
          <button
            className={cn(
              "flex-1 py-3 font-ui text-sm font-semibold transition-colors border-b-2 inline-flex items-center justify-center gap-1.5",
              activeTab === "task"
                ? "border-primary text-primary bg-background"
                : "border-transparent text-muted-foreground",
            )}
            onClick={() => setActiveTab("task")}
          >
            <span>
              {currentChunkType === "fill-in-blank"
                ? t("simulation.active.questions", {
                    defaultValue: "Questions",
                  })
                : t("simulation.active.essay", {
                    defaultValue: "Your Response",
                  })}
            </span>
            {hasUnansweredInCurrentChunk && (
              <span
                className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500"
                aria-label={t("simulation.active.unansweredIndicator", {
                  defaultValue: "Has unanswered tasks",
                })}
              />
            )}
          </button>
        </div>

        {writingPanels}
      </div>

      {/* Essay under-word-limit confirmation dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("simulation.writing.underWordLimit", {
                defaultValue: "Under Word Limit",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("simulation.writing.confirmFinishUnderLimit", {
                count: essayWordCount,
                defaultValue:
                  "You've written {{count}} words. Are you sure you want to submit the Writing module?",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmFinish}
              disabled={isSaving}
            >
              {isSaving
                ? t("common.saving", { defaultValue: "Saving..." })
                : submitModuleConfirmLabel(t, "writing")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* fill-in-blank incomplete confirmation dialog */}
      <AlertDialog
        open={showIncompleteConfirmDialog}
        onOpenChange={setShowIncompleteConfirmDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {incompleteModuleDialogTitle(t, "writing")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("simulation.active.unansweredQuestionsConsequence", {
                count: unansweredNavItems.length,
                module: moduleDisplayName,
                defaultValue:
                  "These {{count}} unanswered responses will be marked as not answered and counted against your score. Are you sure you want to submit the {{module}} module?",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <SimulationUnansweredJumpChips
            items={unansweredNavItems}
            disabled={isSaving}
            onJump={(id) => {
              setShowIncompleteConfirmDialog(false);
              handleWritingNavJump(id);
            }}
          />
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={handleReviewUnanswered}
              disabled={isSaving}
            >
              {t("simulation.active.reviewQuestions", {
                defaultValue: "Review Questions",
              })}
            </AlertDialogAction>
            <AlertDialogAction
              className="border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
              onClick={handleConfirmIncompleteFinish}
              disabled={isSaving}
            >
              {isSaving
                ? t("common.saving", { defaultValue: "Saving..." })
                : submitModuleButtonLabel(t, "writing")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SimulationInactivityDialog
        open={inactivity.isPromptOpen}
        timeRemainingSeconds={state.timeRemaining}
        onContinue={inactivity.continueSession}
        onEnd={handleSaveAndFinish}
      />

      <SimulationSavingOverlay open={isSaving} />
    </div>
  );
}
